import BFLLink from "@repo/shared-ui/BFLLink";

export interface IConsentListItem {
  id?: string;
  text: string;
}

/** Legacy AEM group: `{ item0: { text }, item1: { text } }` */
export interface IConsentList {
  [key: string]: IConsentListItem;
}

export type ConsentListEntry = IConsentListItem | IConsentList;

export interface IBFLConsentModal {
  title: string;
  link: string;
  openInNewTab: string;
  note: string;
  para1: string;
  para2: string;
  /** AEM: `[{ id, text }]` or legacy: `[{ item0: { text } }]` */
  list: ConsentListEntry[];
}

interface IConsentProps {
  ssoContent?: IBFLConsentModal;
}

/** Strip HTML tags in linear time (avoids ReDoS from `/<[^>]+>/`). */
const stripHtmlTags = (text: string) => {
  let out = "";
  let inTag = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === "<") {
      inTag = true;
      continue;
    }
    if (char === ">") {
      inTag = false;
      continue;
    }
    if (!inTag) out += char;
  }
  return out;
};

const cleanText = (text = "") => stripHtmlTags(text).replace(/&amp;/g, "&").trim();

const normalizeConsentItems = (list: ConsentListEntry[] = []): IConsentListItem[] =>
  list.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    if (typeof (entry as IConsentListItem).text === "string") return [entry as IConsentListItem];
    return Object.values(entry).filter(
      (v): v is IConsentListItem => !!v && typeof v === "object" && typeof v.text === "string"
    );
  });

const BFLConsentModal = ({ ssoContent }: IConsentProps) => {
  const { link = "", note = "", para1 = "", para2 = "", list = [] } = ssoContent ?? {};
  const items = normalizeConsentItems(list);

  const renderConsentText = (rawText?: string) => {
    const text = cleanText(rawText);
    if (!text) return null;

    const match = text.match(/\(T&C\)/);
    if (!match) return text;

    const [before, after] = text.split(match[0]);
    return (
      <>
        {before}
        <BFLLink openInNewTab className="text-blue-600 underline hover:text-blue-800" href={link}>
          {match[0]}
        </BFLLink>
        {after}
      </>
    );
  };

  return (
    <>
      <p className="text-center text-sm leading-[16px] text-black">{para1}</p>

      <div className="bg-gray-150 mt-4 w-full space-y-2 rounded-md p-3">
        <p className="text-link-secondary">{note}:</p>
        <ul className="text-link-secondary list-inside list-disc space-y-1 text-sm">
          {items.map((item, index) => (
            <li key={item.id ?? index} className="mb-2">
              {index === 0 ? renderConsentText(item.text) : cleanText(item.text)}
            </li>
          ))}
        </ul>
      </div>

      <p className="mt-4 text-center text-sm text-black">{para2}</p>
    </>
  );
};

export default BFLConsentModal;
